<script setup lang="ts">
import { RouterLink } from 'vue-router'

import UiIcon from '@/components/common/UiIcon.vue'
import { localizedPagePath } from '@/router/pageSlugs'
import type { DesktopNavKey } from './desktopNav'

import { useI18n } from 'vue-i18n'

const props = withDefaults(defineProps<{
  activeDesktopNav?: DesktopNavKey | null
  dropdownsEnabled?: boolean
}>(), {
  activeDesktopNav: null,
  dropdownsEnabled: true,
})

const emit = defineEmits<{
  (e: 'open-desktop-nav', value: DesktopNavKey): void
  (e: 'toggle-desktop-nav', value: DesktopNavKey): void
  (e: 'focus-desktop-nav', value: DesktopNavKey): void
  (e: 'close-desktop-nav'): void
}>()

const { t } = useI18n()

function openDesktopNav(value: DesktopNavKey) {
  if (!props.dropdownsEnabled) return
  emit('open-desktop-nav', value)
}

function focusDesktopNav(value: DesktopNavKey) {
  if (!props.dropdownsEnabled) return
  emit('focus-desktop-nav', value)
}
</script>

<template>
  <nav 
    role="navigation" 
    aria-label="Primary"
    class="nav items primary flex flex-1 justify-end items-center gap-4 lg:gap-12"
  >
    <ul class="flex gap-4 font-medium text-xl leading-none">
      <li>
        <RouterLink
          id="header-gym-trigger"
          :to="localizedPagePath('gymOverview')"
          class="inline-flex items-center gap-2 rounded-md transition-colors duration-200 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          :aria-controls="props.dropdownsEnabled ? 'header-gym-nav' : undefined"
          :aria-haspopup="props.dropdownsEnabled ? 'true' : undefined"
          :aria-expanded="props.dropdownsEnabled ? props.activeDesktopNav === 'gym' : undefined"
          @mouseover="openDesktopNav('gym')"
          @focus="openDesktopNav('gym')"
          @click="emit('close-desktop-nav')"
          @keydown.tab.exact.prevent="focusDesktopNav('gym')"
          @keydown.down.prevent="focusDesktopNav('gym')"
          @keydown.esc.prevent="emit('close-desktop-nav')"
        >
          <span>{{ t('menu.gym') }}</span>
          <UiIcon
            v-if="props.dropdownsEnabled"
            name="ChevronDown"
            :size="18"
            :class="props.activeDesktopNav === 'gym'
              ? 'transition-transform duration-200 rotate-180'
              : 'transition-transform duration-200'"
          />
        </RouterLink>
      </li>
      <li>
        <RouterLink
          id="header-trainer-trigger"
          :to="localizedPagePath('trainerOverview')"
          class="inline-flex items-center gap-2 rounded-md transition-colors duration-200 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          :aria-controls="props.dropdownsEnabled ? 'header-trainer-nav' : undefined"
          :aria-haspopup="props.dropdownsEnabled ? 'true' : undefined"
          :aria-expanded="props.dropdownsEnabled ? props.activeDesktopNav === 'trainer' : undefined"
          @mouseover="openDesktopNav('trainer')"
          @focus="openDesktopNav('trainer')"
          @click="emit('close-desktop-nav')"
          @keydown.tab.exact.prevent="focusDesktopNav('trainer')"
          @keydown.down.prevent="focusDesktopNav('trainer')"
          @keydown.esc.prevent="emit('close-desktop-nav')"
        >
          <span>{{ t('menu.trainer') }}</span>
          <UiIcon
            v-if="props.dropdownsEnabled"
            name="ChevronDown"
            :size="18"
            :class="props.activeDesktopNav === 'trainer'
              ? 'transition-transform duration-200 rotate-180'
              : 'transition-transform duration-200'"
          />
        </RouterLink>
      </li>
      <li>
        <router-link
          id="header-memberships-trigger"
          :to="localizedPagePath('memberships')"
          class="rounded-md transition-colors duration-200 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          @mouseover="emit('close-desktop-nav')"
          @focus="emit('close-desktop-nav')"
          @click="emit('close-desktop-nav')"
        >
          {{ t('menu.memberships') }}
        </router-link>
      </li>
      <li>
        <router-link
          id="header-customer-service-trigger"
          :to="localizedPagePath('customerService')"
          class="rounded-md transition-colors duration-200 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          @mouseover="emit('close-desktop-nav')"
          @focus="emit('close-desktop-nav')"
          @click="emit('close-desktop-nav')"
        >
          {{ t('menu.customerService') }}
        </router-link>
      </li>
    </ul>
  </nav>
</template>
